import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useState } from "react";
import { X } from "lucide-react";
import FloatingADUWizard from "@/components/FloatingADUWizard";
import FloatingReserveButton from "@/components/FloatingReserveButton";
import CanonicalTag from "@/components/CanonicalTag";

const RanchStyleADUDesignAtherton = () => {
  const [showCalendly, setShowCalendly] = useState(false);
  const openCalendlyModal = () => setShowCalendly(true);
  const closeCalendlyModal = () => setShowCalendly(false);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Ranch Style ADU Design & Construction in Atherton",
    "description": "Build beautiful Ranch Style ADUs in Atherton with single-story design, open floor plans, and seamless indoor-outdoor living.",
    "url": "https://www.udaadu.com/ranch-style-adu-design-atherton",
    "mainEntity": {
      "@type": "Service",
      "name": "Ranch Style ADU Design & Construction",
      "provider": {
        "@type": "Organization",
        "name": "UDA ADU"
      },
      "areaServed": "Atherton, CA"
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <CanonicalTag url="https://www.udaadu.com/ranch-style-adu-design-atherton" />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      <Navigation />

      {/* Calendly Modal */}
      {showCalendly && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="relative w-full max-w-2xl h-[70vh] bg-white rounded-lg overflow-hidden shadow-xl">
            <button onClick={closeCalendlyModal} className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors">
              <X className="h-6 w-6 text-gray-600" />
            </button>
            <iframe src="https://calendly.com/udaadu?embed=true&hide_gdpr_banner=1" width="100%" height="100%" frameBorder="0" title="Schedule a meeting" />
          </div>
        </div>
      )}

      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-white">
            Ranch Style ADU Design & Construction in Atherton
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Build beautiful Ranch Style ADUs in Atherton with single-story design, open floor plans, and seamless indoor-outdoor living.
          </p>
        </div>

        <div className="text-center mb-16">
          <Button onClick={openCalendlyModal} size="lg" className="mr-4">
            Get Free Consultation
          </Button>
          <Button asChild variant="outline" size="lg" className="text-white border-white hover:bg-white hover:text-black">
            <Link to="/adu-calculator">Calculate Costs</Link>
          </Button>
        </div>

        {/* Why Choose Ranch Style */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center text-white">Why Choose Ranch Style ADUs in Atherton?</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center p-6 border border-border rounded-lg">
              <h3 className="text-xl font-semibold mb-4 text-white">Single-Story Living</h3>
              <p className="text-muted-foreground">
                Ranch style ADUs offer accessible single-story living with no stairs, perfect for aging in place or universal design in Atherton.
              </p>
            </div>
            <div className="text-center p-6 border border-border rounded-lg">
              <h3 className="text-xl font-semibold mb-4 text-white">Open Floor Plans</h3>
              <p className="text-muted-foreground">
                Expansive open layouts maximize space efficiency while creating flowing, comfortable living areas ideal for modern lifestyles.
              </p>
            </div>
            <div className="text-center p-6 border border-border rounded-lg">
              <h3 className="text-xl font-semibold mb-4 text-white">Indoor-Outdoor Connection</h3>
              <p className="text-muted-foreground">
                Large windows and sliding doors seamlessly connect interior spaces with Atherton's beautiful outdoor environment.
              </p>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center text-white">Ranch Style ADU Features</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-2xl font-semibold mb-4 text-white">Design Elements</h3>
              <ul className="space-y-3 text-muted-foreground">
                <li>• Long, low horizontal lines</li>
                <li>• Large picture windows</li>
                <li>• Covered front porches</li>
                <li>• Attached patios or decks</li>
                <li>• Simple, clean rooflines</li>
              </ul>
            </div>
            <div>
              <h3 className="text-2xl font-semibold mb-4 text-white">Interior Features</h3>
              <ul className="space-y-3 text-muted-foreground">
                <li>• Open concept living areas</li>
                <li>• Vaulted or exposed beam ceilings</li>
                <li>• Built-in storage solutions</li>
                <li>• Large master suites</li>
                <li>• Efficient kitchen layouts</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Cost Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center text-white">Investment Range</h2>
          <div className="text-center p-8 border border-border rounded-lg">
            <p className="text-2xl font-bold mb-4 text-white">$280,000 - $450,000</p>
            <p className="text-muted-foreground">
              Ranch style ADUs offer excellent value with their efficient single-story design and timeless appeal in Atherton's prestigious market.
            </p>
          </div>
        </section>

        {/* Final CTA */}
        <section className="text-center mb-16">
          <h2 className="text-3xl font-bold mb-8 text-white">Start Your Ranch Style ADU Project</h2>
          <p className="text-xl mb-8 text-muted-foreground max-w-3xl mx-auto">
            Ready to build your perfect Ranch style ADU in Atherton? Let's create a single-story living space that combines comfort and style.
          </p>
          <Button onClick={openCalendlyModal} size="lg" className="mr-4">
            Schedule Consultation
          </Button>
          <Button asChild variant="outline" size="lg" className="text-white border-white hover:bg-white hover:text-black">
            <Link to="/adu-calculator">Get Cost Estimate</Link>
          </Button>
        </section>
      </main>

      <FloatingADUWizard />
      <FloatingReserveButton />
      <Footer />
    </div>
  );
};

export default RanchStyleADUDesignAtherton;